import { AdminApiError, type AdminClient, type AbuseControlsOverride } from '@apx/admin-sdk';
import { abuseControlsSchema } from '@apx/integrator-config/schemas';
import { confirmModal } from '../modal.js';
import { showToast } from '../toast.js';

export interface AbusePageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
}

const DEFAULTS = { betCapPerRound: 50, betRateLimitPerSecond: 10 };

/**
 * Wallet anti-abuse control surface.
 *
 *   - Kill switch: immediate single-admin toggle (no two-admin approval).
 *   - Bet cap / rate limit: standard propose-then-approve flow via the
 *     `abuse` config surface.
 */
export async function renderAbusePage(root: HTMLElement, deps: AbusePageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Wallet anti-abuse — ${escapeHtml(deps.gameId)}</h1>
    <p class="page-sub">
      Emergency stop + per-player caps. Kill switch takes effect immediately
      (single admin); caps go through the two-admin approval flow.
    </p>

    <div class="card" id="abuse-kill-card">
      <h2>Kill switch</h2>
      <p class="muted" style="margin-top:-4px">
        Rejects every new bet for this integrator-game pair with
        <code>423 KILL_SWITCH_ENGAGED</code> and pauses the auto-scheduler.
        Use during incident response when an integrator wallet or game logic
        misbehaves.
      </p>
      <div class="row" style="align-items:center;gap:12px;flex-wrap:wrap">
        <span id="abuse-kill-state" class="pill">loading…</span>
        <button id="abuse-kill-engage" class="danger" hidden>Engage kill switch</button>
        <button id="abuse-kill-release" hidden>Release kill switch</button>
      </div>
    </div>

    <div class="card">
      <h2>Per-player caps</h2>
      <p class="muted" style="margin-top:-4px">
        Defaults apply when fields are empty: ${DEFAULTS.betCapPerRound} bets/round,
        ${DEFAULTS.betRateLimitPerSecond} req/sec. Changes require a second admin to approve.
      </p>
      <div class="row" style="gap:14px;flex-wrap:wrap">
        <label style="flex:1 1 220px;display:flex;flex-direction:column;gap:4px">
          <span class="muted" style="font-size:11px">betCapPerRound</span>
          <input id="abuse-cap" type="number" min="1" max="1000" placeholder="${DEFAULTS.betCapPerRound}" />
        </label>
        <label style="flex:1 1 220px;display:flex;flex-direction:column;gap:4px">
          <span class="muted" style="font-size:11px">betRateLimitPerSecond</span>
          <input id="abuse-rate" type="number" min="1" max="1000" placeholder="${DEFAULTS.betRateLimitPerSecond}" />
        </label>
        <div style="display:flex;align-items:flex-end;gap:8px">
          <button id="abuse-save" class="primary">Propose change</button>
        </div>
      </div>
      <p id="abuse-msg" class="muted" style="margin-top:10px"></p>
    </div>
  `;

  const stateEl = root.querySelector<HTMLElement>('#abuse-kill-state')!;
  const engageBtn = root.querySelector<HTMLButtonElement>('#abuse-kill-engage')!;
  const releaseBtn = root.querySelector<HTMLButtonElement>('#abuse-kill-release')!;
  const capEl = root.querySelector<HTMLInputElement>('#abuse-cap')!;
  const rateEl = root.querySelector<HTMLInputElement>('#abuse-rate')!;
  const saveBtn = root.querySelector<HTMLButtonElement>('#abuse-save')!;
  const msgEl = root.querySelector<HTMLElement>('#abuse-msg')!;

  async function refresh(): Promise<void> {
    const { value } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'abuse');
    const v = (value as AbuseControlsOverride | null) ?? {};
    capEl.value = v.betCapPerRound != null ? String(v.betCapPerRound) : '';
    rateEl.value = v.betRateLimitPerSecond != null ? String(v.betRateLimitPerSecond) : '';
    setKillState(Boolean(v.killSwitch));
  }

  function setKillState(engaged: boolean): void {
    stateEl.textContent = engaged ? 'ENGAGED' : 'released';
    stateEl.className = engaged ? 'pill warn' : 'pill live';
    engageBtn.hidden = engaged;
    releaseBtn.hidden = !engaged;
  }

  engageBtn.addEventListener('click', async () => {
    const ok = await confirmModal({
      title: 'Engage kill switch?',
      body: `All new bets for ${deps.gameId} on this integrator will be rejected immediately. Auto-scheduler pauses too. Takes effect with no approval delay.`,
      confirmLabel: 'Engage now',
      danger: true,
    });
    if (!ok) return;
    try {
      await deps.client.setKillSwitch(deps.integratorId, deps.gameId, true);
      showToast('Kill switch engaged.', 'warn');
      setKillState(true);
    } catch (err) {
      showToast(describe(err), 'error');
    }
  });

  releaseBtn.addEventListener('click', async () => {
    const ok = await confirmModal({
      title: 'Release kill switch?',
      body: 'Bets and auto-scheduler resume immediately.',
      confirmLabel: 'Release',
    });
    if (!ok) return;
    try {
      await deps.client.setKillSwitch(deps.integratorId, deps.gameId, false);
      showToast('Kill switch released.', 'success');
      setKillState(false);
    } catch (err) {
      showToast(describe(err), 'error');
    }
  });

  saveBtn.addEventListener('click', async () => {
    msgEl.textContent = '';
    msgEl.className = 'muted';
    const value: AbuseControlsOverride = {};
    if (capEl.value.trim().length > 0) value.betCapPerRound = Number(capEl.value);
    if (rateEl.value.trim().length > 0) value.betRateLimitPerSecond = Number(rateEl.value);
    // Preserve existing killSwitch state if set, since this propose flow
    // touches the whole `abuse` surface.
    const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'abuse');
    const cur = (current as AbuseControlsOverride | null) ?? {};
    if (cur.killSwitch !== undefined) value.killSwitch = cur.killSwitch;
    const parsed = abuseControlsSchema.safeParse(value);
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => e.message).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(deps.integratorId, deps.gameId, 'abuse', parsed.data);
      msgEl.textContent = `Pending approval ${res.approval.id}.`;
    } catch (err) {
      msgEl.textContent = describe(err);
      msgEl.className = 'error';
    }
  });

  await refresh();
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
